import { Injectable } from "@angular/core";
import { CommandsService } from "./commands.service";
import { WatchService } from "./watch.service";
import { LanguageService } from "./language.service";
import { CanvasService } from "./canvas.service";
import { XLogoInterpreter, CallBlock } from "app/xlogo-parser";

import { Token } from "antlr4ts";

declare var ace: any;

@Injectable()
export class DebuggerService {
  // Return values determining the behaviour of the execution after a callback
  static EXECUTION_CONTINUE = 1;
  static EXECUTION_SUSPEND = 0;

  // Components and services provided via constructor
  private watchService: WatchService;

  // Used to mark next and previous commands
  private markerStack = [];
  private markerNext;
  private markerPrev;
  private sessionMarkerNext;
  private sessionMarkerPrev;

  // Used to save contexts and canvases as states for reconstruction
  private interpreterStateStack = [];
  private canvasStateStack = [];

  private editor: any;

  // Used to check wether editor is in debug mode or not
  private debugExecution = false;

  // Attaches services/components and loads images for watch
  constructor(
    private commandsService: CommandsService,
    private lang: LanguageService,
    private canvasService: CanvasService
  ) {
    this.watchService = new WatchService(lang);
  }

  // Extracts a readable string from the constructor of the context
  static getOperationName(ctx): string {
    let operationConstructorString = ctx.constructor
      .toString()
      .substr("function ".length);
    operationConstructorString = operationConstructorString.substr(
      0,
      operationConstructorString.indexOf("(") - 7
    );
    return "visit" + operationConstructorString;
  }

  // Take one step forward in execution
  public stepForward(
    callback: () => void,
    interpreter: XLogoInterpreter
  ): void {
    // Disable buttons during execution of next step
    this.disableButton(this.getNextButton());
    this.disableButton(this.getPrevButton());

    setTimeout(callback, 1);
  }

  // Take one step backwards in execution
  public stepBackward(
    callback: () => void,
    interpreter: XLogoInterpreter
  ): void {
    if (this.getStateStackLength() <= 1) {
      return;
    }

    // Change markers
    this.sessionMarkerNext.removeMarker(this.markerNext);
    this.sessionMarkerPrev.removeMarker(this.markerPrev);
    this.markerStack.pop();
    const coordsNext = this.markerStack.slice(-1)[0];
    const coordsPrev = this.markerStack.slice(-2)[0];
    this.markerPrev = this.highlightRange(
      coordsPrev[0],
      coordsPrev[1],
      coordsPrev[2],
      "markerPrevCmd"
    );
    this.markerNext = this.highlightRange(
      coordsNext[0],
      coordsNext[1],
      coordsNext[2],
      "markerNextCmd"
    );

    // Restore state
    this.interpreterStateStack.pop();
    interpreter.State = this.interpreterStateStack.slice(-1)[0].deepCopy();
    this.canvasStateStack.pop();
    const nextCanvasState = this.canvasStateStack.slice(-1)[0];

    // Restore canvas state
    const nextCanvasStateCopy = this.canvasService.deepCloneState(
      nextCanvasState
    );
    this.canvasService.restoreState(nextCanvasStateCopy);

    // Restore debug watch
    this.watchService.updateDebugWatch(nextCanvasState[0]);

    // Disable step back button if we're on the first command
    if (this.getStateStackLength() <= 1) {
      this.disableButton(this.getPrevButton());
    }
  }

  /*
		Called whenever the visitor hits an internal breakpoint. Debugger decides
		whether execution should just continue (e.g. in nested method calls) or if
		the execution should be suspended. In the latter case, we then wait for
		user input (stepping or aborting the program).
	*/
  public callback(interpreter: XLogoInterpreter): boolean {
    if (!interpreter.State.CurrentInstruction) {
      return false;
    }

    // Update debugger watch
    let canvasState = this.canvasService.getState();
    let turtle = canvasState[0];
    this.watchService.updateDebugWatch(turtle);

    const interpreterState = interpreter.State.deepCopy();

    let curBlock = interpreterState;
    let numberOfCallBlocks = 0;
    while (curBlock && numberOfCallBlocks < 2) {
      if (curBlock instanceof CallBlock) {
        numberOfCallBlocks += 1;
      }
      curBlock = curBlock.ParentBlock;
    }

    if (numberOfCallBlocks >= 2) {
      return false;
    }

    // Save canvas and state of visitor on stacks in order to step back
    this.interpreterStateStack.push(interpreterState);
    this.canvasStateStack.push(canvasState);

    // Enable stepping buttons
    this.enableButton(this.getNextButton());
    if (this.getStateStackLength() > 1) {
      this.enableButton(this.getPrevButton());
    }

    // Update markers for commands
    this.updateMarkers(interpreterState.CurrentInstruction.token);

    // Set cursor position to move the editors
    this.adjustCursorPosition(interpreterState.CurrentInstruction.token);

    return true;
  }

  // Clears memory and removes markers
  public endSession(): void {
    this.interpreterStateStack = [];
    this.canvasStateStack = [];

    this.sessionMarkerNext.removeMarker(this.markerNext);
    this.sessionMarkerPrev.removeMarker(this.markerPrev);
    this.markerStack = [];
  }

  // Returns how many states we currently saved
  public getStateStackLength(): number {
    return this.interpreterStateStack.length;
  }

  // Adjusts the cursor position in the editors to make the next command visible
  private adjustCursorPosition(ctx: Token) {
    const row = ctx.line - 1;
    const colStart = ctx.charPositionInLine;
    const colEnd = colStart + ctx.text.length - 1;

    // Check which editor the command comes from and overshoot cursor position a little
    if (row == 0) {
      const editor = this.commandsService.inputLine;
      editor.selection.moveTo(row, colEnd + 5);
      editor.selection.moveTo(row, colStart - 5);
    } else {
      const editor = this.editor;
      editor.selection.moveTo(row + 2, colEnd);
      editor.selection.moveTo(row - 2, colStart);
    }
  }

  // Updates the editor markers for next and previous commands
  private updateMarkers(ctx: Token): void {
    // Remove old markers
    this.sessionMarkerNext.removeMarker(this.markerNext);
    this.sessionMarkerPrev.removeMarker(this.markerPrev);

    // Add new marker for next command
    let nextRow = ctx.line - 1;
    let nextColStart = ctx.charPositionInLine;
    let nextColEnd = nextColStart + ctx.text.length - 1;
    this.markerNext = this.highlightRange(
      nextRow,
      nextColStart,
      nextColEnd,
      "markerNextCmd"
    );

    // Add new marker for previous command
    if (this.markerStack.length > 0) {
      let oldMarkerCoords = this.markerStack.slice(-1)[0];
      let prevRow = oldMarkerCoords[0];
      let prevColStart = oldMarkerCoords[1];
      let prevColEnd = oldMarkerCoords[2];
      this.markerPrev = this.highlightRange(
        prevRow,
        prevColStart,
        prevColEnd,
        "markerPrevCmd"
      );
    }

    // Update marker stack
    this.markerStack.push([nextRow, nextColStart, nextColEnd]);
  }

  // Highlights a certain range in the editor (or input line) by adding a marker to it
  private highlightRange(row, colStart, colEnd, color): void {
    let Range = (<any>window).ace.require("ace/range").Range;
    let range = new Range(row, colStart, row, colEnd + 1);

    let session;
    if (row == 0) {
      session = this.commandsService.inputLine.session;
    } else {
      session = this.editor.session;
    }
    let marker = session.addMarker(range, color, "text");

    // Save which session this marker belongs to
    if (color == "markerNextCmd") {
      this.sessionMarkerNext = session;
    } else {
      this.sessionMarkerPrev = session;
    }

    return marker;
  }

  public removeMarkers(markers: any[]) {
    if (markers != null) {
      for (var marker of markers) {
        this.editor.session.removeMarker(marker);
      }
    }
  }

  public resetAnnotations(annotations: any[]) {
    this.editor.session.clearAnnotations();
    this.editor.session.setAnnotations(annotations);
  }

  public setInitialSessionMarkers(session: any) {
    this.sessionMarkerNext = session;
    this.sessionMarkerPrev = session;
  }

  public setEditor(editor: any) {
    this.editor = editor;
  }

  public getPrevButton() {
    return document.getElementById("prevButton");
  }

  public getNextButton() {
    return document.getElementById("nextButton");
  }

  public getDebugMode() {
    return this.debugExecution;
  }

  public toggleDebug() {
    this.debugExecution = !this.debugExecution;

    // indicate debug mode by setting toggle colors
    const toggleSwitch = <HTMLInputElement>this.getDebugToggleSwitch();
    toggleSwitch.checked = this.debugExecution;

    // Switch focus to input line
    (<any>window).ace.edit("inputEditor").focus();

    console.log("[ECSV] Debug is on: ", this.debugExecution);
  }

  public disableButton(button) {
    button.disabled = true;
  }

  public enableButton(button) {
    button.disabled = false;
  }

  // Shows button to step forward
  // TODO: only show history if there are multiple commands
  public showDebugElements() {
    console.log("[ECSV] Entering debug mode");
    let debugWatch = document.getElementById("debugWatch");
    let prevButton = document.getElementById("prevButton");
    let nextButton = document.getElementById("nextButton");
    debugWatch.style.display = "block";
    prevButton.classList.remove("hidden");
    nextButton.classList.remove("hidden");

    this.disableButton(this.getDebugToggleButton());
    this.disableButton(this.getDebugToggleSwitch());
  }

  public hideDebugElements() {
    console.log("[ECSV] Exiting debug mode\n");
    let debugWatch = document.getElementById("debugWatch");
    let prevButton = document.getElementById("prevButton");
    let nextButton = document.getElementById("nextButton");
    debugWatch.style.display = "none";
    prevButton.classList.add("hidden");
    nextButton.classList.add("hidden");

    this.enableButton(this.getDebugToggleButton());
    this.enableButton(this.getDebugToggleSwitch());
  }

  // hide all buttons related to debugging
  public hideDebugFunctionality() {
    let debugToggle = document.getElementById("debugToggleButton");
    let prevButton = document.getElementById("prevButton");
    let nextButton = document.getElementById("nextButton");
    debugToggle.classList.add("hidden");
    prevButton.classList.add("hidden");
    nextButton.classList.add("hidden");
  }

  public getDebugToggleButton() {
    return document.getElementById("debugToggleButton");
  }

  public getDebugToggleSwitch() {
    return document.getElementById("debugToggleSwitch");
  }
}



// WEBPACK FOOTER //
// ./src/app/services/debugger.service.ts